/**
 * category-slider.js
 * Auto-slider with drag support, dots, prev/next, responsive card width
 */

function initCategorySlider(section, autoplaySpeed, autoplayEnabled) {
  if (!section) return;

  const track        = section.querySelector('.cat-slider-track');
  const outer        = section.querySelector('.cat-slider-track-outer');
  const btnPrev      = section.querySelector('.cat-slider-btn--prev');
  const btnNext      = section.querySelector('.cat-slider-btn--next');
  const dotsWrap     = section.querySelector('.cat-slider-dots');
  const items        = Array.from(track.querySelectorAll('.cat-slider-item'));

  if (!items.length) return;

  /* ── Responsive card width ── */
  function getCardWidth() {
    const vw = window.innerWidth;
    if (vw <= 360)  return 130;
    if (vw <= 480)  return 160;
    if (vw <= 768)  return 180;
    if (vw <= 1024) return 220;
    return 300;
  }

  function getVisibleCount() {
    const vw = window.innerWidth;
    if (vw <= 480)  return 2.0;
    if (vw <= 640)  return 2.5;
    if (vw <= 768)  return 3.0;
    if (vw <= 1024) return 3.5;
    return 3.8;
  }

  const GAP = 16; // must match CSS gap

  let currentIndex  = 0;
  let autoplayTimer = null;
  let isDragging    = false;
  let dragStartX    = 0;
  let dragDelta     = 0;

  /* ── Apply card widths ── */
  function applyCardWidth() {
    const w = getCardWidth();
    section.style.setProperty('--cat-card-width', w + 'px');
    items.forEach(item => { item.style.width = w + 'px'; });
  }

  /* ── Max index (stop when last card is fully visible) ── */
  function maxIndex() {
    const visible = getVisibleCount();
    return Math.max(0, items.length - Math.floor(visible));
  }

  /* ── Translate track ── */
  function getOffset(index) {
    const w = getCardWidth();
    return index * (w + GAP);
  }

  function goTo(index, animate = true) {
    currentIndex = Math.max(0, Math.min(index, maxIndex()));
    track.style.transition = animate
      ? 'transform 0.45s cubic-bezier(0.25,0.46,0.45,0.94)'
      : 'none';
    track.style.transform = `translateX(-${getOffset(currentIndex)}px)`;
    updateDots();
    updateButtons();
  }

  /* ── Dots ── */
  function buildDots() {
    dotsWrap.innerHTML = '';
    const total = maxIndex() + 1;
    for (let i = 0; i < total; i++) {
      const dot = document.createElement('button');
      dot.className = 'cat-slider-dot' + (i === 0 ? ' active' : '');
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Slide ${i + 1}`);
      dot.addEventListener('click', () => { goTo(i); resetAutoplay(); });
      dotsWrap.appendChild(dot);
    }
  }

  function updateDots() {
    const dots = dotsWrap.querySelectorAll('.cat-slider-dot');
    dots.forEach((d, i) => d.classList.toggle('active', i === currentIndex));
  }

  /* ── Buttons ── */
  function updateButtons() {
    btnPrev.disabled = currentIndex === 0;
    btnNext.disabled = currentIndex >= maxIndex();
  }

  /* ── Autoplay ── */
  function startAutoplay() {
    if (!autoplayEnabled) return;
    autoplayTimer = setInterval(() => {
      const next = currentIndex >= maxIndex() ? 0 : currentIndex + 1;
      goTo(next);
    }, autoplaySpeed || 3000);
  }

  function stopAutoplay() {
    clearInterval(autoplayTimer);
  }

  function resetAutoplay() {
    stopAutoplay();
    startAutoplay();
  }

  /* ── Button events ── */
  btnPrev.addEventListener('click', () => { goTo(currentIndex - 1); resetAutoplay(); });
  btnNext.addEventListener('click', () => { goTo(currentIndex + 1); resetAutoplay(); });

  /* ── Drag / swipe on desktop & touch ── */
  function onDragStart(clientX) {
    isDragging = true;
    dragStartX = clientX;
    dragDelta  = 0;
    track.style.transition = 'none';
    stopAutoplay();
  }

  function onDragMove(clientX) {
    if (!isDragging) return;
    dragDelta = clientX - dragStartX;
    track.style.transform =
      `translateX(${-getOffset(currentIndex) + dragDelta}px)`;
  }

  function onDragEnd() {
    if (!isDragging) return;
    isDragging = false;
    const threshold = getCardWidth() * 0.3;
    if (dragDelta < -threshold) goTo(currentIndex + 1);
    else if (dragDelta > threshold) goTo(currentIndex - 1);
    else goTo(currentIndex);
    resetAutoplay();
  }

  // Mouse
  outer.addEventListener('mousedown',  e => onDragStart(e.clientX));
  window.addEventListener('mousemove', e => { if (isDragging) onDragMove(e.clientX); });
  window.addEventListener('mouseup',   onDragEnd);

  // Touch
  outer.addEventListener('touchstart', e => onDragStart(e.touches[0].clientX), { passive: true });
  outer.addEventListener('touchmove',  e => onDragMove(e.touches[0].clientX),  { passive: true });
  outer.addEventListener('touchend',   onDragEnd);

  // Prevent link click during drag
  items.forEach(item => {
    item.addEventListener('click', e => {
      if (Math.abs(dragDelta) > 10) e.preventDefault();
    });
  });

  /* ── Pause on hover ── */
  section.addEventListener('mouseenter', stopAutoplay);
  section.addEventListener('mouseleave', () => { if (autoplayEnabled) startAutoplay(); });

  /* ── Resize ── */
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      applyCardWidth();
      buildDots();
      goTo(Math.min(currentIndex, maxIndex()), false);
    }, 200);
  });

  /* ── Init ── */
  applyCardWidth();
  buildDots();
  goTo(0, false);
  startAutoplay();
}